import { cn, createEnumSchema, focusRingClassName, tv } from '@joyful/utils'
import type { EnumValueMeta, UserCapability, VariantProps } from '@joyful/utils'
import { CheckIcon } from 'lucide-react'
import {
  Fragment,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import type { NavLinkProps } from 'react-router-dom'
import { NavLink } from 'react-router-dom'

import { getJoyfulAppRuntime } from '../../utils/appContext'
import type { NavbarApp } from '../../utils/appContext'
import type { ButtonProps } from '../../../core'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Ellipsis,
  Item,
  ItemContent,
  ItemEndAdornment,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  Slot,
  Tooltip,
} from '../../../core'
import { useHasUserCapability } from '../../hooks/useHasUserCapability'

const navbarFrameVariants = tv({
  base: 'bg-tremor-background relative shrink-0 border-b',
})

const navbarSectionVariants = tv({
  base: 'flex items-center',

  defaultVariants: {
    gap: 'none',
    share: 'auto',
    visibility: 'always',
  },

  variants: {
    gap: {
      md: 'gap-x-4',
      none: null,
      sm: 'gap-x-3',
    },

    share: {
      auto: null,
      fill: 'min-w-0 flex-1',
      fixed: 'shrink-0',
    },

    visibility: {
      always: null,
      desktop: 'hidden lg:flex',
    },
  },
})

const navbarBrandVariants = tv({
  base: [
    'text-tremor-content-emphasis block font-serif text-nowrap',
    focusRingClassName,
  ],

  defaultVariants: {
    size: 'lg',
  },

  variants: {
    size: {
      lg: 'text-2xl',
      md: 'text-xl',
    },
  },
})

const overflowNavbarLinkListVariants = tv({
  base: 'relative flex min-w-0 flex-1 self-stretch',

  defaultVariants: {
    visibility: 'always',
  },

  variants: {
    visibility: {
      always: null,
      desktop: 'hidden lg:flex',
    },
  },
})

interface NavbarEnvironmentMeta extends EnumValueMeta {
  stripeClassName?: string
}

export const NavbarEnvironmentSchema = createEnumSchema<
  'development' | 'production' | 'staging',
  NavbarEnvironmentMeta
>([
  {
    stripeClassName: 'bg-green-500',
    title: 'Development environment',
    value: 'development',
  },
  { title: 'Production environment', value: 'production' },
  {
    stripeClassName: 'bg-sky-500',
    title: 'Staging environment',
    value: 'staging',
  },
])

export type NavbarEnvironment =
  (typeof NavbarEnvironmentSchema.definitions)[number]['value']

export interface NavbarProps extends Omit<ComponentProps<'nav'>, 'className'> {
  /** Renders a colored edge stripe for non-production environments. */
  environment: NavbarEnvironment
}

export interface NavbarFrameProps extends Omit<
  ComponentProps<'nav'>,
  'className'
> {
  startAdornment?: ReactNode
}

export interface NavbarEnvironmentStripeProps extends Omit<
  ComponentProps<'div'>,
  'className'
> {
  environment: NavbarEnvironment
}

export function NavbarEnvironmentStripe({
  environment,
  ...props
}: NavbarEnvironmentStripeProps) {
  const environmentMeta = NavbarEnvironmentSchema.meta(environment)

  if (!environmentMeta.stripeClassName) {
    return null
  }

  return (
    <div
      className={cn(
        'absolute inset-y-0 left-0 w-1.5',
        environmentMeta.stripeClassName,
      )}
      {...props}
    />
  )
}

export function NavbarFrame({
  children,
  startAdornment,
  ...props
}: NavbarFrameProps) {
  return (
    <nav className={navbarFrameVariants()} {...props}>
      {startAdornment}
      <div className="mx-auto px-4 lg:px-6">
        <div className="flex h-14 justify-between">{children}</div>
      </div>
    </nav>
  )
}

export function Navbar({ children, environment, ...props }: NavbarProps) {
  const environmentMeta = NavbarEnvironmentSchema.meta(environment)

  return (
    <NavbarFrame
      startAdornment={
        environmentMeta.stripeClassName ? (
          <Tooltip content={environmentMeta.title}>
            <NavbarEnvironmentStripe environment={environment} />
          </Tooltip>
        ) : null
      }
      {...props}
    >
      {children}
    </NavbarFrame>
  )
}

export interface NavbarSectionProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof navbarSectionVariants> {}

/** Flex grouping for one side of the navbar (brand + links, or actions). */
export function NavbarSection({
  gap,
  share,
  visibility,
  ...props
}: NavbarSectionProps) {
  return (
    <div
      className={navbarSectionVariants({ gap, share, visibility })}
      {...props}
    />
  )
}

export interface NavbarBrandProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof navbarBrandVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function NavbarBrand({
  asChild = false,
  size,
  ...props
}: NavbarBrandProps) {
  const Component = asChild ? Slot : 'div'
  return <Component className={navbarBrandVariants({ size })} {...props} />
}

export interface NavbarAppMeta extends EnumValueMeta {
  capability: UserCapability
  legacyProductionHost?: string
  localPort: string
  productionHost: string
}

export type { NavbarApp }

export const NavbarAppSchema = createEnumSchema<NavbarApp, NavbarAppMeta>([
  {
    capability: 'app.app.view',
    description: 'Practice dashboards, requests, and documents',
    legacyProductionHost: 'app.joyfulhealth.io',
    localPort: '3000',
    productionHost: 'app.joyfulhealth.com',
    title: 'App',
    value: 'app',
  },
  {
    capability: 'app.bpo.view',
    description: 'Queues, work blocks, and phone operations',
    legacyProductionHost: 'bpo.joyfulhealth.io',
    localPort: '3300',
    productionHost: 'bpo.joyfulhealth.com',
    title: 'BPO',
    value: 'bpo',
  },
  {
    capability: 'app.workspace.view',
    description: 'Agent cases, memories, and automations',
    legacyProductionHost: 'workspace.joyfulhealth.io',
    localPort: '3100',
    productionHost: 'workspace.joyfulhealth.com',
    title: 'Workspace',
    value: 'workspace',
  },
  {
    capability: 'app.internal.view',
    description: 'Admin dashboards, users, and configuration',
    legacyProductionHost: 'internal.joyfulhealth.io',
    localPort: '3200',
    productionHost: 'internal.joyfulhealth.com',
    title: 'Internal',
    value: 'internal',
  },
])

export type NavbarAppDefinition = (typeof NavbarAppSchema.definitions)[number]

export interface NavbarAppSwitcherProps extends Omit<ButtonProps, 'children'> {
  activeApp: NavbarApp
  getPathForApp?: (app: NavbarApp) => string | undefined
}

export function NavbarAppSwitcher({
  activeApp,
  getPathForApp,
  ...buttonProps
}: NavbarAppSwitcherProps) {
  const hasCapability = useHasUserCapability()
  const activeOptionMeta = NavbarAppSchema.meta(activeApp)
  const visibleOptions = NavbarAppSchema.definitions.filter((option) => {
    return hasCapability(option.capability)
  })

  if (visibleOptions.length <= 1) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="brand" size="xs" {...buttonProps} colorVariant="brand">
          {activeOptionMeta.title}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="min-w-48">
        {visibleOptions.map((option) => {
          const isSelected = option.value === activeApp
          const path = getPathForApp?.(option.value)
          return (
            <DropdownMenuItem
              key={option.value}
              asChild
              shouldCloseOnSelect
              endAdornment={
                isSelected ? <CheckIcon className="size-3.5" /> : undefined
              }
              subtitle={option.description}
            >
              <a
                href={getNavbarAppHref({
                  app: option.value,
                  path,
                })}
              >
                {option.title}
              </a>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export interface GetNavbarAppHrefOptions {
  app: NavbarApp
  path?: string
}

export function getNavbarAppHref({ app, path = '/' }: GetNavbarAppHrefOptions) {
  const appMeta = NavbarAppSchema.meta(app)
  const configuredOrigin = getConfiguredNavbarAppOrigin(app)
  if (configuredOrigin) {
    return `${configuredOrigin}${path}`
  }

  const currentLocation = globalThis.location
  if (!isWebProtocol(currentLocation.protocol)) {
    return `https://${appMeta.productionHost}${path}`
  }

  const currentHostname = currentLocation.hostname
  if (isLocalHostname(currentHostname)) {
    return `${currentLocation.protocol}//localhost:${appMeta.localPort}${path}`
  }

  const host = rewriteNavbarAppHostname({
    app,
    fallbackHost: appMeta.productionHost,
    hostname: currentHostname,
  })
  return `${currentLocation.protocol}//${host}${path}`
}

export function getCurrentNavbarApp() {
  const currentLocation = globalThis.location
  const configuredApp = NavbarAppSchema.definitions.find((option) => {
    const configuredOrigin = getConfiguredNavbarAppOrigin(option.value)
    if (!configuredOrigin) {
      return false
    }

    const configuredUrl = new URL(configuredOrigin)
    return (
      configuredUrl.protocol === currentLocation.protocol &&
      configuredUrl.hostname === currentLocation.hostname &&
      configuredUrl.port === currentLocation.port
    )
  })
  if (configuredApp) {
    return configuredApp.value
  }

  if (!isWebProtocol(currentLocation.protocol)) {
    return null
  }

  if (isLocalHostname(currentLocation.hostname)) {
    const currentApp = NavbarAppSchema.definitions.find((option) => {
      return option.localPort === currentLocation.port
    })

    return currentApp?.value ?? getRuntimeNavbarApp()
  }

  return (
    getNavbarAppFromHostname(currentLocation.hostname) ?? getRuntimeNavbarApp()
  )
}

function isLocalHostname(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

function isWebProtocol(protocol: string) {
  return protocol === 'http:' || protocol === 'https:'
}

function getRuntimeNavbarApp() {
  try {
    return getJoyfulAppRuntime().appSurface ?? null
  } catch {
    return null
  }
}

function getConfiguredNavbarAppOrigin(app: NavbarApp) {
  let origin: string | undefined
  try {
    origin = getJoyfulAppRuntime().appUrls.navbarAppOrigins?.[app]
  } catch {
    return null
  }

  if (typeof origin !== 'string' || origin.length === 0) {
    return null
  }

  try {
    return new URL(origin).origin
  } catch {
    return null
  }
}

function rewriteNavbarAppHostname({
  app,
  fallbackHost,
  hostname,
}: {
  app: NavbarApp
  fallbackHost: string
  hostname: string
}) {
  if (parseNavbarAppFromHostname(hostname) === null) {
    return fallbackHost
  }

  const hostParts = hostname.split('.')
  hostParts[0] = app
  return hostParts.join('.')
}

function getNavbarAppFromHostname(hostname: string): NavbarApp | null {
  return parseNavbarAppFromHostname(hostname)
}

function parseNavbarAppFromHostname(hostname: string): NavbarApp | null {
  const hostnameApp = hostname.split('.')[0]
  if (hostnameApp === undefined || !isNavbarApp(hostnameApp)) {
    return null
  }

  return hostnameApp
}

function isNavbarApp(value: string): value is NavbarApp {
  return NavbarAppSchema.definitions.some((definition) => {
    return definition.value === value
  })
}

export type NavbarLinksProps = ComponentProps<'nav'>

// Groups the section's nav links. `self-stretch` makes the group fill the
// navbar's full height (overriding the centering on `NavbarSection`) and
// `-my-px` overlaps the navbar's own bottom border, so each link's active
// `border-b-2` lands flush on the navbar edge instead of floating mid-row.
export function NavbarLinks({ className, ...props }: NavbarLinksProps) {
  return (
    <nav
      className={cn('-my-px flex gap-x-1 self-stretch', className)}
      {...props}
    />
  )
}

export interface OverflowNavbarLinkListLinkItem extends Pick<
  NavbarLinkProps,
  'endAdornment' | 'isActive' | 'to'
> {
  children: ReactNode
  key: string
}

export interface OverflowNavbarLinkListGroupItem {
  children: ReactNode
  isActive?: boolean
  items: ReadonlyArray<OverflowNavbarLinkListLinkItem>
  key: string
}

export type OverflowNavbarLinkListItem =
  | OverflowNavbarLinkListGroupItem
  | OverflowNavbarLinkListLinkItem

export interface OverflowNavbarLinkListProps
  extends
    Omit<ComponentProps<'div'>, 'children' | 'className'>,
    VariantProps<typeof overflowNavbarLinkListVariants> {
  items: ReadonlyArray<OverflowNavbarLinkListItem>
  renderVisibleItem?: (itemKey: string, item: ReactElement) => ReactNode
}

export function OverflowNavbarLinkList({
  items,
  renderVisibleItem,
  visibility,
  ...props
}: OverflowNavbarLinkListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measurementRef = useRef<HTMLDivElement>(null)
  const [measuredVisibleCount, setMeasuredVisibleCount] = useState<
    number | null
  >(null)
  const visibleCount = measuredVisibleCount ?? 0
  const hasMeasured = measuredVisibleCount !== null
  const visibleItems = items.slice(0, visibleCount)
  const overflowItems = items.slice(visibleCount)

  useLayoutEffect(() => {
    const updateVisibleCount = () => {
      const container = containerRef.current
      const measurement = measurementRef.current
      if (!container || !measurement) {
        return
      }

      const linkElements = [
        ...measurement.querySelectorAll<HTMLElement>(
          '[data-overflow-navbar-link-item]',
        ),
      ]
      const overflowTrigger = measurement.querySelector<HTMLElement>(
        '[data-overflow-navbar-link-trigger]',
      )
      if (linkElements.length !== items.length || !overflowTrigger) {
        return
      }

      const gap = Number.parseFloat(getComputedStyle(measurement).columnGap)
      const columnGap = Number.isNaN(gap) ? 0 : gap
      const availableWidth = container.clientWidth
      const linkWidths = linkElements.map((element) => {
        return element.offsetWidth
      })
      const overflowTriggerWidth = overflowTrigger.offsetWidth

      let nextVisibleCount = 0
      for (
        let candidateCount = items.length;
        candidateCount >= 0;
        candidateCount -= 1
      ) {
        const candidateLinkWidth = linkWidths
          .slice(0, candidateCount)
          .reduce((total, width) => {
            return total + width
          }, 0)
        const hasOverflow = candidateCount < items.length
        const renderedItemCount = candidateCount + (hasOverflow ? 1 : 0)
        const candidateGapWidth =
          renderedItemCount > 1 ? columnGap * (renderedItemCount - 1) : 0
        const candidateWidth =
          candidateLinkWidth +
          candidateGapWidth +
          (hasOverflow ? overflowTriggerWidth : 0)

        if (candidateWidth <= availableWidth) {
          nextVisibleCount = candidateCount
          break
        }
      }

      // The measurement pass must update before paint so the right-side navbar
      // actions do not get pushed around by overflowing links.
      setMeasuredVisibleCount((currentVisibleCount) => {
        return currentVisibleCount === nextVisibleCount
          ? currentVisibleCount
          : nextVisibleCount
      })
    }

    const resizeObserver = new ResizeObserver(updateVisibleCount)
    const container = containerRef.current
    const measurement = measurementRef.current
    if (container) {
      resizeObserver.observe(container)
    }

    if (measurement) {
      resizeObserver.observe(measurement)
    }

    updateVisibleCount()
    return () => {
      resizeObserver.disconnect()
    }
  }, [items])

  return (
    <div
      ref={containerRef}
      className={overflowNavbarLinkListVariants({ visibility })}
      {...props}
    >
      <NavbarLinks
        aria-hidden={hasMeasured ? undefined : true}
        className={cn(
          'lg:gap-x-4',
          hasMeasured ? null : 'pointer-events-none invisible',
        )}
      >
        {visibleItems.map((item) => {
          const renderedItem = <OverflowNavbarLink item={item} />
          return renderVisibleItem ? (
            <Fragment key={item.key}>
              {renderVisibleItem(item.key, renderedItem)}
            </Fragment>
          ) : (
            <Fragment key={item.key}>{renderedItem}</Fragment>
          )
        })}

        {overflowItems.length > 0 ? (
          <OverflowNavbarMoreNavigationMenu items={overflowItems} />
        ) : null}
      </NavbarLinks>

      <NavbarLinks
        ref={measurementRef}
        aria-hidden
        className={cn(
          'invisible absolute top-0 left-0 h-0 max-w-none overflow-hidden whitespace-nowrap lg:gap-x-4',
        )}
      >
        {items.map((item) => {
          return (
            <span key={item.key} data-overflow-navbar-link-item>
              <OverflowNavbarLink item={item} />
            </span>
          )
        })}
        <span data-overflow-navbar-link-trigger>
          <OverflowNavbarMoreNavigationMenu items={[]} />
        </span>
      </NavbarLinks>
    </div>
  )
}

function OverflowNavbarLink({
  item,
}: {
  readonly item: OverflowNavbarLinkListItem
}) {
  if ('items' in item) {
    return <OverflowNavbarNavigationMenu item={item} />
  }

  return (
    <NavbarLink
      to={item.to}
      isActive={item.isActive}
      endAdornment={item.endAdornment}
    >
      {item.children}
    </NavbarLink>
  )
}

function OverflowNavbarNavigationMenu({
  item,
}: {
  readonly item: OverflowNavbarLinkListGroupItem
}) {
  return (
    <NavigationMenu className="self-stretch [&>div]:h-full">
      <NavigationMenuList className="h-full">
        <NavigationMenuItem className="flex h-full">
          <NavigationMenuTrigger
            className={cn(
              'border-b-2 px-3 pt-1',
              item.isActive
                ? 'border-b-tremor-brand-subtle text-tremor-content-strong border-transparent'
                : 'border-transparent',
            )}
          >
            {item.children}
          </NavigationMenuTrigger>
          {item.items.length > 0 ? (
            <NavigationMenuContent className="-mt-2">
              <OverflowNavbarMoreNavigationMenuContent items={item.items} />
            </NavigationMenuContent>
          ) : null}
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  )
}

function OverflowNavbarMoreNavigationMenu({
  items,
}: {
  readonly items: ReadonlyArray<OverflowNavbarLinkListItem>
}) {
  return (
    <NavigationMenu className="self-stretch [&>div]:h-full">
      <NavigationMenuList className="h-full">
        <NavigationMenuItem className="flex h-full">
          <NavigationMenuTrigger>{`More`}</NavigationMenuTrigger>
          {items.length > 0 ? (
            <NavigationMenuContent className="-mt-2">
              <OverflowNavbarMoreNavigationMenuContent items={items} />
            </NavigationMenuContent>
          ) : null}
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  )
}

function OverflowNavbarMoreNavigationMenuContent({
  items,
}: {
  readonly items: ReadonlyArray<OverflowNavbarLinkListItem>
}) {
  return items.map((item) => {
    if ('items' in item) {
      return (
        <div key={item.key}>
          <div className="text-tremor-content-subtle px-2 py-1.5 text-xs font-medium">
            {item.children}
          </div>
          <OverflowNavbarMoreNavigationMenuContent items={item.items} />
        </div>
      )
    }

    return (
      <Item key={item.key} asChild isInteractive size="lg">
        <NavigationMenuLink asChild>
          <NavLink
            to={item.to}
            aria-current={item.isActive ? 'page' : undefined}
          >
            {({ isActive }) => {
              const isItemActive = item.isActive ?? isActive
              return (
                <>
                  <ItemContent>
                    <Ellipsis
                      tone={isItemActive ? 'strong' : 'content'}
                      weight="medium"
                    >
                      {item.children}
                    </Ellipsis>
                  </ItemContent>
                  {isValidElement(item.endAdornment) ? (
                    <ItemEndAdornment>{item.endAdornment}</ItemEndAdornment>
                  ) : null}
                </>
              )
            }}
          </NavLink>
        </NavigationMenuLink>
      </Item>
    )
  })
}

export interface NavbarLinkProps extends Omit<NavLinkProps, 'children'> {
  children: ReactNode
  endAdornment?: ReactNode
  /**
   * Overrides NavLink's own active matching. Use when "active" depends on more
   * than the link's path (e.g. a detail route belonging to a section).
   */
  isActive?: boolean
}

export function NavbarLink({
  children,
  className,
  endAdornment,
  isActive: isActiveOverride,
  ...props
}: NavbarLinkProps) {
  return (
    <NavLink
      className={(state) => {
        const isActive = isActiveOverride ?? state.isActive
        return cn(
          isActive
            ? 'border-tremor-brand-subtle text-tremor-content-strong'
            : 'text-tremor-content hover:text-tremor-content-emphasis hover:border-tremor-content-subtle border-transparent',
          'inline-flex items-center gap-x-1.5 border-b-2 px-3 pt-1 text-sm font-medium',
          focusRingClassName,
          typeof className === 'string' ? className : null,
        )
      }}
      {...props}
    >
      {children}
      {endAdornment}
    </NavLink>
  )
}
